import {
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  List,
  message,
  Modal,
  Select,
  Spin,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../UserContext';
import { BaseComponentProps, NativeReminder } from '../../types';
import { formatDateFromUTCtoAgent, getAgentTimezone } from './utils';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface ReminderFormData {
  description: string;
  date: dayjs.Dayjs;
  repeat_every_hours?: number;
  repeat_until?: dayjs.Dayjs;
}

function formatReminderRepeatSummary(
  reminder: NativeReminder,
  agentTimezone: string,
): string | null {
  const everyHours = reminder.repeat_every_hours;
  if (everyHours == null || everyHours <= 0) return null;

  const intervalLabel =
    everyHours === 1 ? 'Every hour' : `Every ${everyHours} hours`;

  const untilPart = reminder.repeat_until
    ? ` until ${formatDateFromUTCtoAgent(reminder.repeat_until, agentTimezone)}`
    : '';
  return `${intervalLabel}${untilPart}`;
}

export const Reminders = ({
  agent,
  apiUrl,
  clientConfig,
}: BaseComponentProps): JSX.Element => {
  const user = useContext(UserContext);
  const [reminders, setReminders] = useState<NativeReminder[] | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [editingReminder, setEditingReminder] = useState<NativeReminder | null>(
    null,
  );
  const [viewingReminder, setViewingReminder] = useState<NativeReminder | null>(
    null,
  );
  const [form] = Form.useForm();
  const agentTimezone = getAgentTimezone(agent, clientConfig);

  const fetchReminders = async (agent: any) => {
    try {
      setLoading(true);
      if (!user?.accessToken) {
        console.error('No access token available');
        return;
      }

      const url = `${apiUrl}/api/agents/${agent.id}/reminders`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${user.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch reminders: ${response.status}`);
      }

      const jsonData = await response.json();
      setReminders(jsonData);
    } catch (err) {
      console.error('An error occurred while fetching reminders:', err);
      setReminders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReminders(agent).then().catch(console.log);
  }, [agent]);

  const handleCreateReminder = () => {
    setEditingReminder(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditReminder = (reminder: NativeReminder) => {
    setEditingReminder(reminder);
    form.setFieldsValue({
      description: reminder.description,
      date: dayjs(reminder.datetime),
      repeat_every_hours: reminder.repeat_every_hours || undefined,
      repeat_until: reminder.repeat_until
        ? dayjs(reminder.repeat_until)
        : undefined,
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values: ReminderFormData) => {
    try {
      if (!user?.accessToken) {
        message.error('No access token available');
        return;
      }
      if (!values.date || !dayjs.isDayjs(values.date)) {
        message.error('Date is required and must be valid');
        return;
      }

      // Format the date/time as a string without timezone information
      // The API will assume the date/time is in the agent's local timezone
      const localDateString = values.date.format('YYYY-MM-DD HH:mm:ss');
      const localRepeatUntilString = values.repeat_until
        ? values.repeat_until.format('YYYY-MM-DD HH:mm:ss')
        : undefined;

      const payload: any = {
        description: values.description,
        date: localDateString,
      };
      if (values.repeat_every_hours) {
        payload.repeat_every_hours = values.repeat_every_hours;
      }
      if (localRepeatUntilString) {
        payload.repeat_until = localRepeatUntilString;
      }

      console.log('Reminder payload:', payload);

      let url: string;
      let method: string;
      if (editingReminder) {
        url = `${apiUrl}/api/tasks/native-reminder`;
        method = 'PUT';
        payload.id = editingReminder.id;
        payload.agent_id = agent.id;
      } else {
        url = `${apiUrl}/api/tasks/${agent.id}/post_task`;
        method = 'POST';
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save reminder');
      }

      message.success(
        editingReminder
          ? 'Reminder updated successfully'
          : 'Reminder created successfully',
      );
      setModalVisible(false);
      form.resetFields();
      await fetchReminders(agent);
    } catch (error) {
      console.error('Error saving reminder:', error);
      message.error(
        error instanceof Error ? error.message : 'Failed to save reminder',
      );
    }
  };

  const handleDeleteReminder = async (reminderId: string) => {
    try {
      if (!user?.accessToken) {
        message.error('No access token available');
        return;
      }

      const reminderToDisable = reminders?.find(r => r.id === reminderId);
      if (!reminderToDisable) {
        message.error('Reminder not found');
        return;
      }

      const response = await fetch(`${apiUrl}/api/tasks/native-reminder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.accessToken}`,
        },
        body: JSON.stringify({
          id: reminderId,
          description: reminderToDisable.description,
          date: reminderToDisable.datetime,
          do_not_send: true,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to disable reminder');
      }

      message.success('Reminder disabled successfully');
      await fetchReminders(agent);
    } catch (error) {
      console.error('Error disabling reminder:', error);
      message.error('Failed to disable reminder');
    }
  };

  const handleEnableReminder = async (reminderId: string) => {
    try {
      if (!user?.accessToken) {
        message.error('No access token available');
        return;
      }

      const reminderToEnable = reminders?.find(r => r.id === reminderId);
      if (!reminderToEnable) {
        message.error('Reminder not found');
        return;
      }

      const response = await fetch(`${apiUrl}/api/tasks/native-reminder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.accessToken}`,
        },
        body: JSON.stringify({
          id: reminderId,
          description: reminderToEnable.description,
          date: reminderToEnable.datetime,
          do_not_send: false,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to enable reminder');
      }

      message.success('Reminder enabled successfully');
      await fetchReminders(agent);
    } catch (error) {
      console.error('Error enabling reminder:', error);
      message.error('Failed to enable reminder');
    }
  };

  const sortedReminders = reminders
    ?.slice()
    .sort(
      (a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime(),
    );

  return (
    <div>
      <Spin spinning={loading}>
        <Flex style={{ height: '70vh', overflowY: 'auto' }} vertical>
          <Flex
            align="center"
            justify="space-between"
            style={{ marginBottom: '16px' }}
          >
            <Button onClick={() => void fetchReminders(agent)}>Refresh</Button>
            <Button onClick={handleCreateReminder} type="primary">
              Create Reminder
            </Button>
          </Flex>

          {reminders && reminders.length === 0 && (
            <Text style={{ margin: '20px 0', textAlign: 'center' }}>
              No reminders found for this agent.
            </Text>
          )}

          <List
            dataSource={sortedReminders}
            itemLayout="vertical"
            renderItem={reminder => {
              const isDisabled = reminder.do_not_send === true;
              const repeatSummary = formatReminderRepeatSummary(
                reminder,
                agentTimezone,
              );

              return (
                <List.Item
                  extra={
                    <div style={{ textAlign: 'right' }}>
                      <Flex gap="small" vertical>
                        {isDisabled && <Tag color="red">Disabled</Tag>}
                        <Flex gap="small">
                          <Button
                            onClick={() => setViewingReminder(reminder)}
                            size="small"
                          >
                            View
                          </Button>
                          <Button
                            onClick={() => handleEditReminder(reminder)}
                            size="small"
                          >
                            Edit
                          </Button>
                          {isDisabled ? (
                            <Button
                              onClick={() => handleEnableReminder(reminder.id)}
                              size="small"
                              type="primary"
                            >
                              Enable
                            </Button>
                          ) : (
                            <Button
                              danger
                              onClick={() => handleDeleteReminder(reminder.id)}
                              size="small"
                            >
                              Disable
                            </Button>
                          )}
                        </Flex>
                      </Flex>
                    </div>
                  }
                  style={{
                    background: isDisabled ? '#f0f0f0' : '#f5f5f5',
                    padding: '16px',
                    borderRadius: '8px',
                    margin: '8px 0',
                    opacity: isDisabled ? 0.7 : 1,
                  }}
                >
                  <List.Item.Meta
                    description={
                      <div>
                        <Text
                          strong
                          style={{ color: isDisabled ? '#999' : undefined }}
                        >
                          {reminder.description}
                        </Text>
                      </div>
                    }
                    title={
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div
                          style={{
                            color: isDisabled ? '#999' : undefined,
                            textAlign: 'left',
                          }}
                        >
                          <div>
                            Scheduled for:{' '}
                            {formatDateFromUTCtoAgent(
                              reminder.datetime,
                              agentTimezone,
                            )}
                          </div>
                          {repeatSummary && (
                            <div>Repeats: {repeatSummary}</div>
                          )}
                        </div>
                        <Text type="secondary">ID: {reminder.id}</Text>
                      </div>
                    }
                  />
                  <div
                    style={{
                      marginTop: '10px',
                      fontSize: '12px',
                      color: '#888',
                    }}
                  >
                    <div>
                      Created:{' '}
                      {formatDateFromUTCtoAgent(
                        reminder.created_at,
                        agentTimezone,
                      )}{' '}
                      | Last Updated:{' '}
                      {formatDateFromUTCtoAgent(
                        reminder.updated_at,
                        agentTimezone,
                      )}
                      {reminder.sequence_number !== undefined &&
                        ` | Sequence Number: ${reminder.sequence_number}`}
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        </Flex>
      </Spin>

      <Modal
        footer={null}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        open={modalVisible}
        title={editingReminder ? 'Edit Reminder' : 'Create New Reminder'}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input placeholder="Enter reminder description" />
          </Form.Item>

          <Form.Item
            label="Date & Time"
            name="date"
            rules={[
              { required: true, message: 'Please select a date and time' },
            ]}
          >
            <DatePicker
              format="YYYY-MM-DD HH:mm:ss"
              onChange={value => {
                form.setFieldsValue({
                  date: value ? value.tz(agentTimezone, true) : value,
                });
              }}
              placeholder="Select date and time"
              showTime
              style={{ width: '100%' }}
              value={
                form.getFieldValue('date')
                  ? dayjs.tz(form.getFieldValue('date'), agentTimezone)
                  : undefined
              }
            />
          </Form.Item>

          <Form.Item
            label="Repeat every (hours)"
            name="repeat_every_hours"
            tooltip="Leave blank for no repeat"
          >
            <Input min={1} placeholder="e.g. 24 for daily" type="number" />
          </Form.Item>

          <Form.Item
            label="Repeat until"
            name="repeat_until"
            tooltip="Leave blank for no repeat end date"
          >
            <DatePicker
              format="YYYY-MM-DD HH:mm:ss"
              onChange={value => {
                form.setFieldsValue({
                  repeat_until: value ? value.tz(agentTimezone, true) : value,
                });
              }}
              placeholder="Select repeat end date and time"
              showTime
              style={{ width: '100%' }}
              value={
                form.getFieldValue('repeat_until')
                  ? dayjs.tz(form.getFieldValue('repeat_until'), agentTimezone)
                  : undefined
              }
            />
          </Form.Item>

          <Form.Item>
            <Flex gap="small" justify="end">
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button htmlType="submit" type="primary">
                {editingReminder ? 'Update' : 'Create'}
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Reminder Modal */}
      <Modal
        footer={null}
        onCancel={() => setViewingReminder(null)}
        open={viewingReminder != null}
        title="Reminder Details"
        width={600}
      >
        {viewingReminder && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <Text strong>Description:</Text>{' '}
              {viewingReminder.description || '—'}
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text strong>Scheduled for:</Text>{' '}
              {viewingReminder.datetime
                ? formatDateFromUTCtoAgent(
                    viewingReminder.datetime,
                    agentTimezone,
                  )
                : '—'}
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text strong>ID:</Text> {viewingReminder.id || '—'}
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text strong>Created:</Text>{' '}
              {viewingReminder.created_at
                ? formatDateFromUTCtoAgent(
                    viewingReminder.created_at,
                    agentTimezone,
                  )
                : '—'}
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text strong>Last Updated:</Text>{' '}
              {viewingReminder.updated_at
                ? formatDateFromUTCtoAgent(
                    viewingReminder.updated_at,
                    agentTimezone,
                  )
                : '—'}
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text strong>Sequence Number:</Text>{' '}
              {viewingReminder.sequence_number !== undefined &&
              viewingReminder.sequence_number !== null
                ? viewingReminder.sequence_number
                : '—'}
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text strong>Hardcoded Message:</Text>{' '}
              {viewingReminder.hardcoded_message || '—'}
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text strong>Repeat Every (hours):</Text>{' '}
              {viewingReminder.repeat_every_hours !== undefined &&
              viewingReminder.repeat_every_hours !== null
                ? viewingReminder.repeat_every_hours
                : '—'}
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text strong>Repeat Until:</Text>{' '}
              {viewingReminder.repeat_until
                ? formatDateFromUTCtoAgent(
                    viewingReminder.repeat_until,
                    agentTimezone,
                  )
                : '—'}
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text strong>Metadata:</Text>
              {viewingReminder.meta ? (
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {Object.entries(viewingReminder.meta).map(([key, value]) => (
                    <li key={key}>
                      <Text type="secondary">{key}: </Text>
                      <Text code>
                        {typeof value === 'object'
                          ? JSON.stringify(value)
                          : String(value)}
                      </Text>
                    </li>
                  ))}
                </ul>
              ) : (
                '—'
              )}
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text strong>Status:</Text>{' '}
              {viewingReminder.do_not_send ? 'Disabled' : 'Active'}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
